import { TimeAgo } from '@/components/time-ago';
import { Audience } from '@/models/audiences';
import { ConnectorFlowInstanceDTO, ConnectorFlowInstanceJobStatusDTO } from '@/models/connectors';
import { DistributionChannel } from '@/models/distributionChannels';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { CONNECTOR_TYPE_CUSTOM } from './constants';
import { CustomConnectorResourceColumn } from './CustomConnectorResourceColumn';
import { DestinationNameColumn } from './DestinationNameColumn';
import { AudienceDestinationDisconnectColumn } from './columns/AudienceDestinationDisconnectColumn';
import { AudienceFlowInstanceDisconnectColumn } from './columns/AudienceFlowInstanceDisconnectColumn';
import { JobStatusColumn } from './columns/JobStatusColumn';

export type UnifiedDestinationData = {
  id: string;
  name: string;
  type: string;
  destination: string;
  createdOn: string;
  data: DistributionChannel | ConnectorFlowInstanceDTO;
  jobStatus?: ConnectorFlowInstanceJobStatusDTO;
};

const columnHelper = createColumnHelper<UnifiedDestinationData>();

export const getDestinationTableColumns = (t: TFunction, audience: Audience, isWorkspaceAdmin: boolean) => {
  const columns: ColumnDef<UnifiedDestinationData>[] = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => t('audience_details:destinations.table.audience_name'),
      cell: ({ row }) => <DestinationNameColumn row={row} />,
      minSize: 300,
      maxSize: 300,
    }),
    columnHelper.accessor('destination', {
      id: 'destination',
      header: () => t('audience_details:destinations.table.resource'),
      cell: ({ getValue, row }) => {
        if (row.original.type === CONNECTOR_TYPE_CUSTOM) {
          return <CustomConnectorResourceColumn flowInstance={row.original.data as ConnectorFlowInstanceDTO} />;
        }
        return <span className='text-sm text-gray-600'>{getValue() || '-'}</span>;
      },
      minSize: 200,
      maxSize: 200,
    }),
    columnHelper.display({
      id: 'status',
      header: () => t('audience_details:destinations.table.status'),
      cell: ({ row }) => <JobStatusColumn jobStatus={row.original.jobStatus} isCustomConnector={row.original.type === CONNECTOR_TYPE_CUSTOM} />,
      minSize: 180,
      maxSize: 180,
    }),
    columnHelper.accessor('createdOn', {
      id: 'createdOn',
      header: () => t('audience_details:destinations.table.created_at'),
      cell: ({ getValue }) => (
        <div className='text-sm text-gray-600'>
          <TimeAgo time={getValue()} />
        </div>
      ),
      minSize: 150,
      maxSize: 150,
    }),
  ] as ColumnDef<UnifiedDestinationData>[];

  if (isWorkspaceAdmin) {
    columns.push(
      columnHelper.display({
        id: 'rapidActions',
        header: () => t('COMMON:table.rapidActions'),
        cell: ({ row }) => {
          if (row.original.type === CONNECTOR_TYPE_CUSTOM) {
            return <AudienceFlowInstanceDisconnectColumn audience={audience} flowInstance={row.original.data as ConnectorFlowInstanceDTO} />;
          }
          return <AudienceDestinationDisconnectColumn audience={audience} destination={row.original.data as DistributionChannel} />;
        },
        minSize: 120,
        maxSize: 120,
        meta: {
          className: () => 'font-semibold',
          align: 'center',
        },
      })
    );
  }

  return columns;
};
